import * as PopoverPrimitive from '@radix-ui/react-popover';
import { XClose } from '@untitled-ui/icons-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import type { OnboardingStep } from '../services/onboarding-tooltip';
import { tGlobal } from '../services/translation';
import { Button } from './buttons/button';

export interface LightTooltipProps {
  targetId: string;
  title: string;
  description: string;
  isOpen: boolean;
  onClose: () => void;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  showArrow?: boolean;
  animationDuration?: number;
  className?: string;
}

export interface OnboardingTooltipProps {
  steps: OnboardingStep[];
  isActive: boolean;
  currentStep: number;
  onNext: () => void;
  onClose: () => void;
  animationDuration?: number;
}

const slideOffsets: Record<string, { x: string; y: string }> = {
  top: { x: '0', y: '4px' },
  bottom: { x: '0', y: '-4px' },
  left: { x: '4px', y: '0' },
  right: { x: '-4px', y: '0' },
};

interface TooltipContentProps {
  title: string;
  description: string;
  onClose: () => void;
  side: 'top' | 'bottom' | 'left' | 'right';
  align: 'start' | 'center' | 'end';
  showArrow: boolean;
  animationDuration: number;
  footer?: React.ReactNode;
  className?: string;
}

const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  function TooltipContent(props, ref) {
    const {
      title,
      description,
      onClose,
      side,
      align,
      showArrow,
      animationDuration,
      footer,
      className,
    } = props;

    const offset = slideOffsets[side] ?? slideOffsets.top;

    return (
      <PopoverPrimitive.Content
        ref={ref}
        className={twMerge(
          'z-50 flex max-w-72 flex-col gap-4 rounded-lg border-none bg-gradient-to-r from-[rgb(65,71,193)] to-[rgb(50,44,131)] p-4 text-left shadow-tooltip',
          className
        )}
        sideOffset={8}
        side={side}
        align={align}
        style={
          {
            '--tooltip-duration': `${animationDuration}ms`,
            '--tooltip-slide-x': offset.x,
            '--tooltip-slide-y': offset.y,
          } as React.CSSProperties
        }
        onOpenAutoFocus={function (e) {
          e.preventDefault();
        }}
      >
        {/* Header with close button */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p className="font-body-1-sb text-white">{title}</p>
          </div>
          <button
            onMouseDown={function (e) {
              e.preventDefault();
              onClose();
            }}
            className="flex-shrink-0 text-white"
            aria-label="Close"
          >
            <XClose className="h-4 w-4" />
          </button>
        </div>

        {/* Description */}
        <p className="font-body-3-r text-white">{description}</p>

        {/* Optional footer */}
        {footer}

        {showArrow ? (
          <PopoverPrimitive.Arrow className="z-999 border-none fill-[rgb(65,71,193)]" />
        ) : null}
      </PopoverPrimitive.Content>
    );
  }
);

interface TooltipWrapperProps extends TooltipContentProps {
  targetElement: HTMLElement;
}

function TooltipWrapper(props: TooltipWrapperProps) {
  const { targetElement, className, ...contentProps } = props;
  const rect = targetElement.getBoundingClientRect();

  return (
    <PopoverPrimitive.Root open={true}>
      <PopoverPrimitive.Anchor
        style={{
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          pointerEvents: 'none',
        }}
      />
      <PopoverPrimitive.Portal>
        <TooltipContent className={className} {...contentProps} />
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export function OnboardingTooltip(props: OnboardingTooltipProps) {
  const { steps, isActive, currentStep, onNext, onClose, animationDuration = 150 } = props;

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  const currentStepData = steps[currentStep] ?? null;
  const totalSteps = steps.length;

  useEffect(
    function () {
      if (!isActive || currentStepData == null) {
        setTargetElement(null);
        return;
      }

      // Find the target element by data attribute
      const element = document.querySelector<HTMLElement>(
        `[data-onboarding-id="${currentStepData.targetId}"]`
      );

      if (element != null) {
        setTargetElement(element);
      } else {
        // Target element not found, tooltip will not render
        setTargetElement(null);
      }
    },
    [isActive, currentStepData]
  );

  // Don't render if not active or no step data
  if (!isActive || currentStepData == null || targetElement == null) {
    return null;
  }

  const side = currentStepData.side ?? 'top';
  const align = currentStepData.align ?? 'center';
  const showArrow = currentStepData.showArrow ?? true;

  const footer =
    totalSteps > 1 ? (
      <div className="flex items-center justify-between gap-2">
        <span className="font-body-3-m text-white">
          {tGlobal('stepOf', { current: currentStep + 1, total: totalSteps })}
        </span>
        <Button variant="outline" size="lg" onClick={onNext}>
          {tGlobal('next')}
        </Button>
      </div>
    ) : null;

  return createPortal(
    <TooltipWrapper
      targetElement={targetElement}
      title={currentStepData.title}
      description={currentStepData.description}
      onClose={onClose}
      side={side}
      align={align}
      showArrow={showArrow}
      animationDuration={animationDuration}
      footer={footer}
    />,
    document.body
  );
}

export function LightTooltip(props: LightTooltipProps) {
  const {
    targetId,
    title,
    description,
    isOpen,
    onClose,
    side = 'top',
    align = 'center',
    showArrow = true,
    animationDuration = 150,
    className = '',
  } = props;

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  useEffect(
    function () {
      if (!isOpen) {
        setTargetElement(null);
        return;
      }

      // Find the target element by data attribute with retry logic
      const findElement = function () {
        const element = document.querySelector<HTMLElement>(`[data-onboarding-id="${targetId}"]`);

        if (element != null) {
          setTargetElement(element);
        } else {
          // Retry after a short delay if element not found
          const retryTimer = setTimeout(function () {
            const retryElement = document.querySelector<HTMLElement>(
              `[data-onboarding-id="${targetId}"]`
            );
            if (retryElement != null) {
              setTargetElement(retryElement);
            } else {
              setTargetElement(null);
            }
          }, 50);

          return function () {
            clearTimeout(retryTimer);
          };
        }
      };

      const cleanup = findElement();
      return cleanup;
    },
    [isOpen, targetId]
  );

  // Don't render if not open or no target element
  if (!isOpen || targetElement == null) {
    return null;
  }

  return createPortal(
    <TooltipWrapper
      targetElement={targetElement}
      title={title}
      description={description}
      onClose={onClose}
      side={side}
      align={align}
      showArrow={showArrow}
      animationDuration={animationDuration}
      className={className}
    />,
    document.body
  );
}
